import React, { useEffect, useRef, useState } from 'react';

// THE OUTLINER — ported whole from legacy/app.js (2026-08-23, final phase).
// Actions (and habits) only; goals are transparent — their work lifts to
// their own level, so every indent is a step under a step. All the .ol-*
// dress in legacy/style.css applies unchanged.
//
// THE ENGINE KEEPS THE TREE + THE ORDER: mutations go through window globals
// so _mission (the agent's context) and this view can never disagree —
// olApplyReorder (splice + persist + announce), olCommitAdd (POST + local
// append + order), afterNodeWrite (patch + re-pull), refreshMissionDetail.
// Reads come through window too (canonStatus, tagsForNode, deadlineLabel,
// olActionDeadline, isHabitScheduledToday, nextScheduledDayLabel,
// assigneeAvatarsHtml) — one vocabulary, no second copy.

// NO HABIT HERE, on purpose (2026-09-20). This popover mints a node from a
// title alone — olCommitAdd posts {kind, title} and nothing else — and a habit
// born that way has no cadence. Cadence is what makes a habit a habit: without
// one it never virtualizes into Now (server.js, isHabitScheduledForDate returns
// false on a null cadence), Lookback skips it, and the sheet can only say "pick
// a cadence to start tracking consistency". The option even described itself as
// "a recurring action with a cadence" while asking for neither.
//
// Habits are still made, by the two paths that carry a cadence through:
//   · the agent's json:create block, which emits cadence + schedule with the node
//   · the sheet's "make recurring" on an action, which lands you on the new
//     habit's sheet to pick one ("You'll pick the cadence next")
// Cadence is not defaulted to daily anywhere, by design — so the fix is to stop
// minting cadence-less habits, not to guess one.
//
// This is the POPOVER's vocabulary only. Sheet.jsx keeps its own OL_TYPES copy
// with habit in it: that one renders an EXISTING node's glyph and label, and
// dropping habit there would strip the ↻ off every habit already made.
export const OL_TYPES = [
  { key: 'goal', label: 'goal', glyph: '◇',
    desc: "a thing you're tending to or working toward — has a status, may have a target",
    example: '"good communication" (ongoing), "land a job by Q3" (bounded)' },
  { key: 'action', label: 'action', glyph: '→',
    desc: "a discrete thing you'll do — has a checkbox",
    example: '"reply to his text", "research the company"' },
];

// The builder's type chooser (goal / action) — the detail page never
// opens one ("+" goes straight to an action draft; goals are the plan's).
export function TypePopover({ onPick, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  return (
    <div ref={ref} className="ol-type-popover" style={{ position: 'absolute' }}>
      {OL_TYPES.map((t) => (
        <button key={t.key} className="ol-type-opt"
                onClick={(e) => { e.stopPropagation(); onClose(); onPick(t.key); }}>
          <span className="opt-label">{t.label}</span>
          <span className="opt-desc">{t.desc}</span>
          <span className="opt-example">{t.example}</span>
        </button>
      ))}
    </div>
  );
}

// A FINISHED BRANCH is a done action with nothing open left under it. That is
// the whole rule for what the Completed fold may take (owner call 2026-10-26):
// a done parent with open children stays in the list, because folding it would
// put live work inside a collapsed section — the one thing a done pile must
// never hide. A habit is never finished; it recurs, so it is never in here.
export function isFinishedBranch(entry) {
  const canon = window.canonStatus?.(entry.node.status);
  if (entry.node.kind !== 'action') return false;
  if (!['completed', 'not_needed'].includes(canon)) return false;
  return (entry.children || []).every(isFinishedBranch);
}

export function collectActionTree(nodes) {
  const out = [];
  for (const n of (nodes || [])) {
    const kids = collectActionTree(n.children || []);
    if (n.kind === 'action' || n.kind === 'habit') out.push({ node: n, children: kids });
    else out.push(...kids); // a goal is transparent here
  }
  return out;
}

function localYmd(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ── Pointer drag: ghost + before/after/INTO indicator ────────────────────────
// A drop has three meanings, read from where the pointer sits on the row
// beneath it (2026-09-20, "can we make the drag gesture support nesting"):
//   · its top band     → before  (reorder among siblings — the legacy law)
//   · its bottom band  → after   (same)
//   · its middle band  → INTO    (re-parent: the dragged row becomes its child)
// before/after still resolve against SIBLINGS only, so the tree order stays
// the engine's. INTO can land on ANY row — except the source and its own
// descendants, since a node cannot be made a child of itself.
const INTO_BAND = 0.3; // the middle 40% of a row is the "into" zone
function startRowDrag(downEvent, row, siblingIds, sourceId, rootEl, onDrop, suppress) {
  const siblingRows = siblingIds
    .filter((id) => id !== sourceId)
    .map((id) => rootEl.querySelector(`.ol-row[data-id="${CSS.escape(id)}"]`))
    .filter(Boolean);
  // the source's own unit wraps it and everything under it — nothing in there
  // can become its parent
  const sourceUnit = row.closest('.ol-unit');
  const intoRows = [...rootEl.querySelectorAll('.ol-row')]
    .filter((r) => r !== row && !(sourceUnit && sourceUnit.contains(r)));
  const rect = row.getBoundingClientRect();
  const offsetX = downEvent.clientX - rect.left;
  const offsetY = downEvent.clientY - rect.top;
  let ghost = null, started = false, target = null;
  try { row.setPointerCapture(downEvent.pointerId); } catch {}

  const ensureStarted = () => {
    if (started) return;
    started = true;
    ghost = row.cloneNode(true);
    ghost.classList.add('ol-ghost');
    ghost.style.width = rect.width + 'px';
    ghost.style.height = rect.height + 'px';
    document.body.appendChild(ghost);
    row.classList.add('dragging');
    document.body.classList.add('ol-dragging');
  };
  const clearIndicators = () => {
    rootEl.querySelectorAll('.ol-row.drop-before, .ol-row.drop-after, .ol-row.drop-into')
      .forEach((r) => r.classList.remove('drop-before', 'drop-after', 'drop-into'));
  };
  const updateTarget = (clientY) => {
    clearIndicators();
    // INTO first: is the pointer inside the middle band of a row it may nest under?
    for (const ir of intoRows) {
      const r = ir.getBoundingClientRect();
      if (clientY < r.top || clientY > r.bottom) continue;
      const band = r.height * INTO_BAND;
      if (clientY >= r.top + band && clientY <= r.bottom - band) {
        ir.classList.add('drop-into');
        target = { row: ir, side: 'into' };
        return;
      }
      break; // on a row, but at its edge — fall through to before/after
    }
    // otherwise the legacy nearest-sibling reorder
    let nearest = null, nearestDist = Infinity;
    for (const sr of siblingRows) {
      const r = sr.getBoundingClientRect();
      const mid = r.top + r.height / 2;
      const dist = Math.abs(clientY - mid);
      if (dist < nearestDist) { nearestDist = dist; nearest = { row: sr, mid }; }
    }
    if (nearest) {
      const side = clientY < nearest.mid ? 'before' : 'after';
      nearest.row.classList.add(side === 'before' ? 'drop-before' : 'drop-after');
      target = { row: nearest.row, side };
    } else target = null;
  };
  const onMove = (e) => {
    if (!started) {
      const dx = e.clientX - downEvent.clientX;
      const dy = e.clientY - downEvent.clientY;
      if (dx * dx + dy * dy < 9) return;
      ensureStarted();
    }
    ghost.style.left = (e.clientX - offsetX) + 'px';
    ghost.style.top = (e.clientY - offsetY) + 'px';
    updateTarget(e.clientY);
  };
  const cleanup = () => {
    row.removeEventListener('pointermove', onMove);
    row.removeEventListener('pointerup', onUp);
    row.removeEventListener('pointercancel', onUp);
    try { row.releasePointerCapture(downEvent.pointerId); } catch {}
    if (ghost) ghost.remove();
    row.classList.remove('dragging');
    document.body.classList.remove('ol-dragging');
    clearIndicators();
  };
  const onUp = () => {
    const wasDrag = started;
    const committed = started && target;
    const t = target;
    cleanup();
    if (wasDrag) suppress();
    if (!committed) return;
    setTimeout(() => onDrop(t.row.dataset.id, t.side), 0);
  };
  row.addEventListener('pointermove', onMove);
  row.addEventListener('pointerup', onUp);
  row.addEventListener('pointercancel', onUp);
}

// ── Right-click quick menu (action rows) ─────────────────────────────────────
function OlCtxMenu({ menu, onClose }) {
  const ref = useRef(null);
  const { node, x, y } = menu;
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    const r = el.getBoundingClientRect();
    if (r.right > window.innerWidth - 8) el.style.left = `${window.innerWidth - r.width - 8}px`;
    if (r.bottom > window.innerHeight - 8) el.style.top = `${window.innerHeight - r.height - 8}px`;
  }, [x, y]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const friday = new Date(today);
  const dow = friday.getDay();
  friday.setDate(today.getDate() + (dow <= 5 ? (5 - dow) : (5 - dow + 7)));
  const cur = node.target_date || null;
  const dot = (matches) => (matches ? <span className="ctx-current">●</span> : null);

  const act = async (fn) => { onClose(); await fn(); };
  const patch = (p) => act(async () => {
    try {
      await window.putJson(`/api/items/${node.id}`, { value_json: p });
      await window.afterNodeWrite(node.id, p);
    } catch (e) { alert(`Couldn't update — ${e.message}`); }
  });

  return (
    <div ref={ref} className="ol-ctxmenu">
      <div className="ol-ctxmenu-group">
        <div className="ol-ctxmenu-label">Set due</div>
        <button className="ol-ctxmenu-item" onClick={() => patch({ target_date: localYmd(today) })}>Today {dot(cur === localYmd(today))}</button>
        <button className="ol-ctxmenu-item" onClick={() => patch({ target_date: localYmd(tomorrow) })}>Tomorrow {dot(cur === localYmd(tomorrow))}</button>
        <button className="ol-ctxmenu-item" onClick={() => patch({ target_date: localYmd(friday) })}>This week {dot(cur === localYmd(friday))}</button>
        <button className="ol-ctxmenu-item" onClick={() => act(async () => window.openSheet?.(node.id))}>Pick a date…</button>
      </div>
      <div className="ol-ctxmenu-group">
        <button className="ol-ctxmenu-item" onClick={() => patch({ status: 'completed' })}>Mark complete</button>
        <button className="ol-ctxmenu-item danger" onClick={() => act(async () => {
          if (!confirm(`Delete "${node.title || 'this action'}"?`)) return;
          try {
            const res = await fetch(window.maybeScopeUrl(`/api/items/${node.id}`), { method: 'DELETE', credentials: 'same-origin' });
            if (!res.ok) throw new Error(res.statusText);
            await window.refreshMissionDetail();
          } catch (e) { alert(`Couldn't delete — ${e.message}`); }
        })}>Delete</button>
      </div>
    </div>
  );
}

// ── The inline draft — a line you are typing on ──────────────────────────────
function InlineDraft({ parentId, kind, onDone, onCancel }) {
  const ref = useRef(null);
  const submitting = useRef(false);
  useEffect(() => { ref.current?.focus(); }, []);
  const commit = async () => {
    if (submitting.current) return;
    const title = (ref.current?.textContent || '').trim();
    if (!title) { onCancel(); return; }
    submitting.current = true;
    try {
      await window.olCommitAdd(parentId, kind, title);
      onDone();
    } catch (err) {
      alert(`Create failed: ${err.message}`);
      submitting.current = false;
    }
  };
  return (
    <div className="ol-unit">
      <div className={`ol-row is-${kind === 'goal' ? 'pursuit' : kind}`}>
        <div className="ol-body">
          <span className="ol-input-group">
            <span className="ol-draft-type">{kind}</span>
            <span ref={ref} className="ol-name" contentEditable data-placeholder={`untitled ${kind}`} spellCheck={false}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commit(); }
                    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
                  }}
                  onBlur={() => setTimeout(() => {
                    if (submitting.current) return;
                    if (!(ref.current?.textContent || '').trim()) onCancel(); else commit();
                  }, 80)} />
          </span>
        </div>
      </div>
    </div>
  );
}

// THE ROW'S RIGHT-HAND COLUMN IS THE DATE, AND ONLY THE DATE (owner call
// 2026-09-20, "just put the deadline on the right"). It carried the habit's
// consistency tally too — kept/total, set as "10/12" — which beside the word
// "today" reads as a second date (October 12th), not as ten of twelve. The
// tally is the sheet's: the habit's history strip is where consistency lives,
// with the room to say what it means. Here: when it is due, and nothing else.
function HabitMeta({ node }) {
  // "today or the date, without colour or a mark" (owner call 2026-09-20): the
  // habit's meta reads exactly like an action's — a plain word for when, in
  // the meta voice. No ✓, no →, no accent; done and pending are not told apart
  // here (the sheet's history strip does that). The press-to-log affordance
  // stays on a pending one, revealed by the cursor and the title, not by ink.
  if (node.status === 'paused') return <span className="ol-meta">paused</span>;
  if (!node.cadence) return null;
  if (window.isHabitScheduledToday?.(node)) {
    if (node.today_status === 'completed') return <span className="ol-meta">today</span>;
    return (
      <span className="ol-meta ol-habit-log" title="click to mark today done"
            onClick={(e) => { e.stopPropagation(); window.logHabitToday?.(node.id); }}>
        today
      </span>
    );
  }
  const nextDay = window.nextScheduledDayLabel?.(node);
  return <span className="ol-meta">{nextDay || node.cadence}</span>;
}

function Avatars({ node }) {
  const html = window.assigneeAvatarsHtml?.(node) || '';
  return html ? <span dangerouslySetInnerHTML={{ __html: html }} /> : null;
}

function OlRow({ node, siblings, rootRef, suppressRef, onCtx, onAddChild, onReorder }) {
  const type = node.kind === 'goal' ? 'pursuit' : node.kind;
  // THE BOX ANSWERS THE PRESS, NOT THE NETWORK (2026-10-26, "it takes a long
  // time to hit the checks"). The tick used to await PUT /api/items/:id before
  // anything on screen moved, and that PUT is not one hop: the server reads the
  // node back first to build the merge (server.js, PUT /api/items/:id), so the
  // box sat unchanged through a read AND a write to Paradigm — with no pending
  // state to say so, which made one tick read as a click that missed and a run
  // of them read as a hang. The intent paints here; the write runs behind it.
  //
  // The override is held only until the engine's own copy agrees: afterNodeWrite
  // patches _mission and re-announces, so `stored` catches up a moment later and
  // this lets go. It is a held press, not a second source of truth for status.
  const [optimistic, setOptimistic] = useState(null);
  const stored = window.canonStatus?.(node.status);
  const canon = optimistic ?? stored;
  useEffect(() => {
    if (optimistic !== null && stored === optimistic) setOptimistic(null);
  }, [stored, optimistic]);
  const isDoneAction = type === 'action' && ['completed', 'not_needed'].includes(canon);
  const rowRef = useRef(null);
  // One row's writes run in order. Painting instantly invites a fast second
  // press, and two PUTs in flight for the same node can land either way round —
  // so the chain, not the wire, decides which status is written last.
  const writeChain = useRef(Promise.resolve());

  const dl = type === 'action' ? window.olActionDeadline?.(node)
    : type === 'pursuit' ? window.deadlineLabel?.(node.target_date) : null;
  // the held press when there is one, so a ticked in-progress action loses its
  // "in progress" word with the same press that strikes it through
  const shownStatus = optimistic ?? node.status;
  const statusWord = type === 'action' && !dl && shownStatus && shownStatus !== 'open' && shownStatus !== 'completed'
    ? shownStatus.replace(/_/g, ' ') : null;
  const rowTags = window.tagsForNode?.(node) || [];

  return (
    <div ref={rowRef}
         className={`ol-row is-${type} committed${isDoneAction ? ' done' : ''}`}
         data-id={node.id}
         onClick={(e) => {
           if (suppressRef.current === node.id) { suppressRef.current = null; e.stopPropagation(); return; }
           if (e.target.closest('.ol-add-child')) return;
           window.openSheet?.(node.id);
         }}
         onContextMenu={type === 'action' ? (e) => {
           e.preventDefault(); e.stopPropagation();
           onCtx(node, e.clientX, e.clientY);
         } : undefined}
         onPointerDown={(e) => {
           if (e.button !== 0) return;
           if (e.target.closest('.ol-add-child')) return;
           if (e.target.closest('.ol-bullet')) return; // the checkbox is a press, not a grab
           if (e.target.closest('[contenteditable]')) return;
           e.preventDefault();
           startRowDrag(e, rowRef.current, siblings, node.id, rootRef.current,
             (targetId, side) => onReorder(node.id, targetId, side),
             () => { suppressRef.current = node.id; });
         }}>
      <span className="ol-grab" title="drag to reorder">⋮⋮</span>
      {/* THE BOX IS A CHECKBOX (2026-09-20). It was a round bullet — a ring that
          filled when the action was done — and the radius sweep squared it, so
          it began to look like a checkbox while still being a span with no
          handler. A square outline that fills IS the drawing of a checkbox; the
          honest fix is to make it one, not to put the circle back. An action
          toggles completed/open. A habit logs today, the same as pressing its
          "today" meta. The row's own pointerdown (the drag) steps aside for it. */}
      <span className="ol-bullet"
            role={type === 'pursuit' ? undefined : 'checkbox'}
            aria-checked={type === 'pursuit' ? undefined : isDoneAction}
            title={type === 'habit' ? 'log today' : isDoneAction ? 'mark open' : 'mark complete'}
            onClick={type === 'pursuit' ? undefined : (e) => {
              e.stopPropagation();
              if (type === 'habit') { window.logHabitToday?.(node.id); return; }
              const status = isDoneAction ? 'open' : 'completed';
              const was = optimistic;
              setOptimistic(status);
              writeChain.current = writeChain.current.then(async () => {
                try {
                  await window.putJson(`/api/items/${node.id}`, { value_json: { status } });
                  await window.afterNodeWrite?.(node.id, { status });
                } catch (err) {
                  // put the box back where the press found it and say so. The row
                  // is already drawn as done; a swallowed failure here would leave
                  // a lie on the page that survives until the next full pull.
                  setOptimistic(was);
                  alert(`Couldn't update — ${err.message}`);
                }
              });
            }}>{type === 'pursuit' ? '' : '•'}</span>
      <div className="ol-body">
        <span className="ol-name">{node.title}</span>
        {rowTags.length ? (
          <span className="ol-tags">
            {rowTags.map((t) => (
              <span key={t.id} className="tag-chip" data-color={t.color || 'sand'}>
                <span className="tag-chip-label">{t.label}</span>
              </span>
            ))}
          </span>
        ) : null}
        <span className="ol-add-child" title="add a child"
              onClick={(e) => { e.stopPropagation(); onAddChild(node.id); }}>+</span>
        {type === 'pursuit' && <span className="ol-rule" />}
        <span className="ol-meta-group">
          {dl && <span className={`ol-meta${dl.warn ? ' warn' : ''}`}>{dl.text}</span>}
          {statusWord && <span className="ol-meta">{statusWord}</span>}
          {type === 'habit' && <HabitMeta node={node} />}
          <Avatars node={node} />
        </span>
      </div>
    </div>
  );
}

function OlTree({ rows, parentId, rootRef, suppressRef, onCtx, addingUnder, setAddingUnder, onReorder, onAdded }) {
  const siblings = rows.map((r) => r.node.id);
  return (
    <>
      {rows.map((r) => (
        <div className="ol-unit" key={r.node.id}>
          <OlRow node={r.node} siblings={siblings} rootRef={rootRef} suppressRef={suppressRef}
                 onCtx={onCtx} onAddChild={(id) => setAddingUnder(id)}
                 onReorder={(from, to, side) => onReorder(parentId, siblings, from, to, side)} />
          {(r.children.length || addingUnder === r.node.id) ? (
            <div className="ol-children">
              <OlTree rows={r.children} parentId={r.node.id} rootRef={rootRef} suppressRef={suppressRef}
                      onCtx={onCtx} addingUnder={addingUnder} setAddingUnder={setAddingUnder}
                      onReorder={onReorder} onAdded={onAdded} />
              {addingUnder === r.node.id && (
                <InlineDraft parentId={r.node.id} kind="action"
                             onDone={() => { setAddingUnder(null); onAdded(); }}
                             onCancel={() => setAddingUnder(null)} />
              )}
            </div>
          ) : null}
        </div>
      ))}
    </>
  );
}

// Whichever way you leave the done pile is remembered per browser — the same
// law the Now tail keeps, and one key for every mission: the fold is a reading
// habit, not a property of the list you happen to be looking at.
const DONE_FOLD_KEY = 'missions_new:outlinerDoneOpen';

export default function Outliner({ mission, pickType = false }) {
  const rootRef = useRef(null);
  const suppressRef = useRef(null);
  const [ctx, setCtx] = useState(null);
  // which node an inline draft is open under; ROOT = the mission itself.
  // { parentId, kind } — kind is 'action' except the builder's root picker.
  const [adding, setAdding] = useState(null);
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const [doneOpen, setDoneOpen] = useState(() => {
    try { return localStorage.getItem(DONE_FOLD_KEY) === '1'; } catch { return false; }
  });
  const addingUnder = adding?.parentId ?? null;
  const setAddingUnder = (parentId) => setAdding(parentId == null ? null : { parentId, kind: 'action' });

  const tree = collectActionTree(mission.items || []);
  // THE DONE PILE (owner call 2026-10-26, "crossed out tasks automatically show
  // up in a completed actions underneath"). A list that is mostly strikethrough
  // is a list you have to read past to find the work, so the finished branches
  // gather at the foot under one line that says how many.
  //
  // Only the TOP LEVEL is split. A finished branch deeper in the tree sits under
  // a parent that is still open and still on the page, and lifting it out from
  // under a row you can see would cost it the only thing that says what it was
  // for. It stays where it is, struck through, with its parent.
  const live = tree.filter((r) => !isFinishedBranch(r));
  const finished = tree.filter(isFinishedBranch);
  const toggleDone = () => setDoneOpen((v) => {
    try { localStorage.setItem(DONE_FOLD_KEY, v ? '0' : '1'); } catch {}
    return !v;
  });

  // A before/after drop reorders WITHIN the sibling list (the legacy law). The
  // tree shown here skips goals, so the visible siblings may span different
  // real parents — resolve through the engine, which knows the true lists.
  //
  // An INTO drop re-parents: the same PUT /api/items/:id/parent the sheet's
  // move picker calls, followed by the same refresh, so the two roads to
  // nesting cannot drift apart. The engine's tree is re-pulled rather than
  // patched locally: a re-parent moves a whole subtree, and the order table
  // under the new parent is the server's to settle.
  const onReorder = async (parentId, siblings, fromId, targetId, side) => {
    if (side !== 'into') { window.olReorder?.(fromId, targetId, side); return; }
    try {
      await window.putJson(`/api/items/${fromId}/parent`, { parent_id: targetId });
      window.invalidateMissionCache?.();
      await window.refreshMissionDetail?.();
    } catch (e) { alert(`Couldn't nest — ${e.message}`); }
  };

  return (
    <div ref={rootRef} className={`ol-root${tree.length ? '' : ' is-empty'}`}>
      <OlTree rows={live} parentId={mission.id} rootRef={rootRef} suppressRef={suppressRef}
              onCtx={(node, x, y) => setCtx({ node, x, y })}
              addingUnder={addingUnder} setAddingUnder={setAddingUnder}
              onReorder={onReorder} onAdded={() => {}} />
      {addingUnder === mission.id && (
        <InlineDraft parentId={mission.id} kind={adding.kind}
                     onDone={() => setAdding(null)}
                     onCancel={() => setAdding(null)} />
      )}
      <div className="ol-add-root" style={pickType ? { position: 'relative' } : undefined}>
        <button onClick={(e) => {
          e.stopPropagation();
          if (pickType) setTypePickerOpen(true);
          else setAddingUnder(mission.id);
        }}>
          <span className="glyph">+</span>
          <span className="label">{pickType ? 'add a goal or action' : 'add an action'}</span>
        </button>
        {typePickerOpen && (
          <TypePopover onClose={() => setTypePickerOpen(false)}
                       onPick={(kind) => setAdding({ parentId: mission.id, kind })} />
        )}
      </div>
      {/* The head is the handle, wearing the kicker's clothes and the same +/−
          the Now tail wears. OPEN, the rows are ORDINARY OUTLINER ROWS — same
          checkbox (so one press puts a thing back in the list), same context
          menu, same nesting. The pile is a fold, not a second rendering. */}
      {finished.length > 0 && (
        <div className={`ol-done${doneOpen ? ' open' : ''}`}>
          <button type="button" className="ol-done-head" aria-expanded={doneOpen} onClick={toggleDone}>
            <span className="ol-done-caret" aria-hidden="true">{doneOpen ? '−' : '+'}</span>
            Completed
            <span className="ol-done-count">{finished.length}</span>
          </button>
          {doneOpen && (
            <OlTree rows={finished} parentId={mission.id} rootRef={rootRef} suppressRef={suppressRef}
                    onCtx={(node, x, y) => setCtx({ node, x, y })}
                    addingUnder={addingUnder} setAddingUnder={setAddingUnder}
                    onReorder={onReorder} onAdded={() => {}} />
          )}
        </div>
      )}
      {ctx && <OlCtxMenu menu={ctx} onClose={() => setCtx(null)} />}
    </div>
  );
}
